import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApplePayConfig, BraintreeCreditCardType, GooglePayConfig, OpenNotesPaymentInfoListItem, PaymentType } from '../models/payment';
import { OpenNotesQuickPayPaymentInfoListItem } from '../models/quickpay';

import * as braintree from 'braintree-web';
import * as $ from 'jquery';

@Injectable({
  providedIn: 'root'
})
export class BraintreeService {

  constructor() { }

  /**
   * Routine to create a list of selected program codes used by the one-time payment process
   *
   * @param openNotesPaymentInfoListItem - Array of selected one-time payment process items
   *
   * @returns An array of program codes
   */
  public extractLenderProgramCodes(openNotesPaymentInfoListItem: OpenNotesPaymentInfoListItem[]) {
    let programCodes: string[] = [];

    openNotesPaymentInfoListItem.forEach((openNotes) => {
      programCodes.push(openNotes.loanProgram);
    })

    return programCodes;
  }

  /**
   *  Routine to obtain the Google Pay configuration
   *
   * @returns Google Pay configuration for TEST or PRODUCTION
   */
  public getGooglePayConfig(): GooglePayConfig {
    let googlePayConfig: GooglePayConfig = {
      isTestEnvironment: environment.braintree.googlePay.isTestEnvironment,
      googleMerchantId: environment.braintree.googlePay.googleMerchantId,
      testNonce: environment.braintree.googlePay.testNonce
    };

    return googlePayConfig;
  }

  /**
   * Routine to obtain the Apple Pay configuration
   *
   * @returns Apple Pay configuration
   */
  public getApplePayConfig(): ApplePayConfig {
    let applePayConfig: ApplePayConfig = {
        version: environment.braintree.applePay.version,
        label: environment.braintree.applePay.label,
        testNonce: environment.braintree.applePay.testNonce
    };

    return applePayConfig;
  }

  /**
   * Routine to create a list of selected program codes used by the one-time payment process
   *
   * @param openNotesQuickPayPaymentInfoListItem - Array of selected one-time payment process items
   *
   * @returns An array of program codes
   */
  public extractQuickPayLenderProgramCodes(openNotesQuickPayPaymentInfoListItem: OpenNotesQuickPayPaymentInfoListItem[]) {
    let programCodes: string[] = [];

    openNotesQuickPayPaymentInfoListItem.forEach((openNotes) => {
      programCodes.push(openNotes.loanProgram);
    })

    return programCodes;
  }

  /**
   * Routine to determine the lender program code to connect to Braintree on the client side for its APIs
   * as well as the server side for its APIs.  When it is a loan group, it will determine the mix of loans and select
   * the appropriate loan program code (priority 1, priority 2 or default).
   *
   * @param loanPrograms - Array of loan program codes
   *
   * @returns Braintree lender program code to be used on Client and Server side to communicate with Braintree
   */
  public getBrainTreeLenderProgramCode(loanPrograms: string[]): string {
    let lenderProgramCode: string = loanPrograms[0];

    // Is this a loan group?
    if (loanPrograms.length > 1) {
      let loanProgramMap: Map<string, number> = new Map();

      // Determine the varieties of loan groups
      loanPrograms.forEach(lp => {
        if (loanProgramMap.has(lp)) {
          let currentCount = loanProgramMap.get(lp);
          loanProgramMap.set(lp, currentCount++);
        } else {
          loanProgramMap.set(lp, 1);
        }
      });

      // Per Jason, mix loan groups which don't exist as of today 5/2023, would default to
      // Tuition Options Braintree account if it were to occur. 'TO' and 'NL' will use same Braintree account.

      // This is a loan group and it has different loan programs. Determine the appropriate account.
      // Although 'TO' and 'NL' use the same Braintree account, this may not be the case in the future.
      // 'TO' will be the primary priority followed by 'NL' but defaulting to 'TO'.
      if (loanProgramMap.size > 1) {
        if (loanProgramMap.has(environment.braintree.lenderProgramPriorityOne)) {
          lenderProgramCode = environment.braintree.lenderProgramPriorityOne;

        } else if (loanProgramMap.has(environment.braintree.lenderProgramPriorityTwo)) {
          lenderProgramCode = environment.braintree.lenderProgramPriorityTwo;

        } else {
          lenderProgramCode = environment.braintree.lenderProgramPriorityOne;
        }
      }
    }

    return lenderProgramCode;
  }

  /**
   * Event fired when a Braintree's hosted field receives focus event.
   *
   * @param field - Hosted Field that received focus
   * @param emittedBy - Field key that emitted the event (i.e. 'expirationMonth...); Braintree field key
   */
  public onFocus(field: any, emittedBy: string) {
    const label = this.findLabel(field);
    const domField = this.findField(field);
    const domErrorField = this.findError(field);
    const domExpErrorField = this.findExpirationError();

    // Simulate mat-form-field move label up except expiration date (date contains placeholder or default value)
    if (emittedBy === 'expirationMonth' || emittedBy === 'expirationYear') {
      var monthLabel = this.findLabelById('expiration-month');
      var yearLabel = this.findLabelById('expiration-year');
      monthLabel.addClass('moveUp');
      yearLabel.addClass('moveUp');
    } else {
      label.addClass('moveUp');
    }

    if (field.isEmpty) {
      label.addClass('label-float').removeClass('filled');
    } else {
      if (field.isValid) {
        label.addClass('label-float').removeClass('filled');
      } else {
        label.removeClass('label-float');

        // Invalid Credit Card property
        this.applyInvalidCreditCard(field, label, domField, domErrorField, domExpErrorField);
      }
    }
  }

  /**
   * Event fired when a Braintree's hosted field receives blur event.
   *
   * @param field - Hosted Field that received blur event
   * @param emittedBy - Field key that emitted the event (i.e. 'expirationMonth...); Braintree field key
   */
  public onBlur(field: any, emittedBy: string) {
    const label = this.findLabel(field);
    const domField = this.findField(field);
    const domErrorField = this.findError(field);
    const domExpErrorField = this.findExpirationError();

    // Simulate mat-form-field functionality move label down when input empty except expiration date (date contains placeholder or default value)
    if (field.isEmpty) {
      if (emittedBy === 'expirationMonth' || emittedBy === 'expirationYear') {
        var monthLabel = this.findLabelById('expiration-month');
        var yearLabel = this.findLabelById('expiration-year');
        monthLabel.removeClass('moveUp');
        yearLabel.removeClass('moveUp');
      } else {
        label.removeClass('moveUp');
      }
    }

    if (field.isValid) {
      label.addClass('filled');

      // Required because mult validations are called one for month another for year
      // Logically one expiration date but two fields.
      if (emittedBy === 'expirationMonth' || emittedBy === 'expirationYear') {
        var monthLabel = this.findLabelById('expiration-month');
        var yearLabel = this.findLabelById('expiration-year');
        monthLabel.addClass('filled');
        yearLabel.addClass('filled');
      }
    } else {

      // Invalid Credit Card property
      this.applyInvalidCreditCard(field, label, domField, domErrorField, domExpErrorField);
    }
  }

  /**
   * Event fired when a Braintree's hosted field receives empty event.
   *
   * @param field - Hosted Field that recevied empty event
   */
  public onEmpty(field: any) {
    const label = this.findLabel(field);
    const domField = this.findField(field);
    const domErrorField = this.findError(field);

    if (field.isValid) {
      label.removeClass('invalid');
    } else {
      label.addClass('invalid');

      // The validityChange event doesn't get fired and applying
      // custom class didn't show border-bottom, so force style.
      domField.css('border-bottom', '2px solid #d32f2f');

      // All credit card fields are required;
      domErrorField.text("This is required");
    }
  }

  /**
   * Event fired when Braintree fires the validity check on a hosted field.
   *
   * @param field - Hosted Field that received validity change event
   * @param emittedBy - Field key that emitted the event (i.e. 'expirationMonth...); Braintree field key
   * @param braintreeFieldFocus - The field key that received 'focus' last.
   */
  public onValidityChange(field: any, emittedBy: string, braintreeFieldFocus: string) {
    const label = this.findLabel(field);
    const domField = this.findField(field);
    const domErrorField = this.findError(field);
    const domExpErrorField = this.findExpirationError();

    // Required when invalid CC number; Correct CC after moving away from CC to hightlight label.
    // Expiration dates different fields but treated as one by Braintree.  Validation will be called on each causing
    // label for both to be highlighted when on one or another; however, blur will be called once resulting in one
    // of the values to be still highlighted.
    if (field.container.id !== 'expiration-month' && field.container.id !== 'expiration-year') {
      label.addClass('label-float');
    } else if (braintreeFieldFocus === 'expirationMonth') {

      // Scenario: Select Month then Year (expired); Go back to Month to correct; Validation is done in order Month then Year
      var monthLabel = this.findLabelById('expiration-month');
      var yearLabel = this.findLabelById('expiration-year');
      monthLabel.addClass('label-float');
      yearLabel.addClass('filled');
    }

    if (field.isPotentiallyValid) {
      label.removeClass('invalid');

      domField.removeAttr('style');
      domErrorField.text('');

      if (emittedBy === 'expirationMonth' || emittedBy == 'expirationYear') {
        domExpErrorField.text('');
      }

    } else {

      // Invalid Credit Card property
      this.applyInvalidCreditCard(field, label, domField, domErrorField, domExpErrorField);
    }
  }

  /**
   * Routine to teardown Braintree. Cleanly remove anything set up by create.
   *
   * @param hostedFieldsInstance - Hosted field instance
   */
  public tearDownBraintree(hostedFieldsInstance: braintree.HostedFields) {
    if (hostedFieldsInstance != null) {
      hostedFieldsInstance.teardown((teardownError) => {
        if (teardownError) {
          console.error("Could not tear down Hosted Fields!");
        }
      });
    }
  }

  /**
   * Routine to determine Braintree's fields are valid
   *
   * @param hostedFieldsInstance - Hosted field instance
   *
   * @returns true - if hosted fields are valid; false - if hosted fields are invalid
   */
  public isBraintreeFieldsValid(hostedFieldsInstance: braintree.HostedFields) {
    let isValid = false;

    if (hostedFieldsInstance != null) {
      let state = hostedFieldsInstance.getState();

       isValid = Object.keys(state.fields).every( (key) => {
        return state.fields[key].isValid;
      });

    }

    return isValid;
  }

  /**
   * Routine to enable/disable a button specifically the 'Submit Payment' button.  This needs to be done
   * programmically because we have a mixture of Reactive form controls and Non-Reactive form controls (HostedFields).
   *
   * @param button - Button being enabled/disabled
   * @param isCreditCardFieldsValid - HostedFields valid flag
   * @param isFormFieldsValid - Reactive form control valid flag
   */
  public enableDisablePaymentButton(button: HTMLButtonElement, isCreditCardFieldsValid: boolean, isFormFieldsValid) {
    if (isCreditCardFieldsValid && isFormFieldsValid) {
      button.disabled = false;
   } else {
    button.disabled = true;
   }
  }

  /**
   * Routine to obtain the servicing company value.
   *
   * Braintree's credit card payments defined in the control panel based on the merchant.
   * One credit card grouping so use Braintree's card type for the transformation after tokenization.
   *
   * @param braintreeCardType - Card type after tokenization (Type retrieved from vault is numeric)
   * @returns Servicing Company (Codes from previous code base before transitioning to Braintree)
   */
  public getServicingCompany(braintreeCardType: string) : string {
    let servicingCompany: string = null;
    switch (braintreeCardType) {
      case BraintreeCreditCardType.Visa:
        servicingCompany = 'VI';
        break;
      case BraintreeCreditCardType.MasterCard:
        servicingCompany = 'MC';
        break;
      case BraintreeCreditCardType.AmericanExpress:
        servicingCompany = 'AE';
        break;
      case BraintreeCreditCardType.Discover:
        servicingCompany = 'DI';
        break;
      default:
        servicingCompany = null;
        break;
    }

    return servicingCompany;
  }

  /**
   * Routine to obtain the payment type description.
   *
   * @param paymentType - Payment type (Credit Card or Electronic Check)
   * @param braintreeCardType - Braintree's card type (From tokenization response or vault)
   *
   * @returns Payment type description
   */
  public getPaymentTypeDescription(paymentType: PaymentType, braintreeCardType: string) : string {
    let paymentTypeDescription: string = "";

    if (paymentType === PaymentType.ElectronicCheck) {
      paymentTypeDescription = 'Electronic Check';
    } else {
      paymentTypeDescription = braintreeCardType;
    }

    return paymentTypeDescription;
  }

  // Routine to obtain Braintree's hosted field label - field.container.id will be the container defined in field properties
  private findLabel(field: any) {

    // Different class for expiration date (No move up/down functionality)
    if (field.container.id === 'expiration-month' || field.container.id === 'expiration-year') {
      return $('.hosted-exp-field--label[for="' + field.container.id + '"]');
    }

    return $('.hosted-field--label[for="' + field.container.id + '"]');
  }

  // Routine to obtain Braintree's hosted field label - passing in field.container.id as a string
  private findLabelById(id: string) {

    // Different class for expiration date (No move up/down functionality)
    if (id === 'expiration-month' || id === 'expiration-year') {
      return $('.hosted-exp-field--label[for="' + id + '"]');
    }

    return $('.hosted-field--label[for="' + id + '"]');
  }

  // Routine to find Braintree's hosted field
  private findField(field: any) {
    return $('#' + field.container.id);
  }

  // Routine to obtain the error container in the DOM for a particular hosted field (CC # or CVV)
  private findError(field: any) {
    return $('#' + field.container.id + '-error');
  }

  // Routine to obtain the error container for expiration date (2 fields but 1 error message row in DOM)
  private findExpirationError() {
    return $('#expiration-date-error');
  }

  /**
   * Routine to apply the invalid classes, messages to a given credit card property.  Note, there are no checks
   * for specific credit card types (i.e. visa, discover...).  The payment types are controlled within braintree's
   * control panel.  Validation of the card is done by Braintree.
   *
   * @param field - Object from Braintree's event handlers
   * @param label - Reference to the label within the DOM
   * @param domField - Reference to Braintree's input field in the DOM (Can't obtain value but required to apply a style)
   * @param domErrorField - Reference to the error message area in the DOM (Credit card number or CVV)
   * @param domExpErrorField - Reference to the error message area in the DOM (Expiration Date)
   */
  private applyInvalidCreditCard(field: any, label: any, domField: any, domErrorField: any, domExpErrorField: any) {
    label.addClass('invalid');

    // Only way to force invalid border
    domField.css('border-bottom', '2px solid #d32f2f');

    if (field.isEmpty) {
      domErrorField.text("This is required");
    } else {
      if (field.container.id === 'credit-card-number') {
        domErrorField.text("The number is not valid for the selected credit card");
      } else if (field.container.id === 'cvv') {
        domErrorField.text("The CVV is not valid");
      } else if (field.container.id === 'expiration-month' || field.container.id === 'expiration-year') {
        domExpErrorField.text("Expiration date expired");
      }
    }
  }

}
